import React, { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { DateInput, VesselSelect } from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { CrewDataTablePrompt, ReportTextCell, todayIso, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import { formatStatNum, KpiTile, StatsKpiListLayout } from './statisticsShared';
import type {
  StatsBreakdownDetailRow,
  StatsBreakdownGroupBy,
  StatsBreakdownKind,
  StatsBreakdownResponse,
  StatsBreakdownRow} from './types';

const KIND_META: Record<
  StatsBreakdownKind,
  { title: string; body: string; valueLabel: string; needsPeriod: boolean }
> = {
  age: {
    title: 'Age statistics',
    body: 'Age of currently onboard crew (from birth date), broken down by the selected dimension.',
    valueLabel: 'Age',
    needsPeriod: false},
  'sign-on': {
    title: 'Sign-on statistics',
    body: 'Sign-on events in the selected period, broken down by the selected dimension.',
    valueLabel: 'Count',
    needsPeriod: true},
  'sign-off': {
    title: 'Sign-off statistics',
    body: 'Sign-off events in the selected period, broken down by the selected dimension.',
    valueLabel: 'Count',
    needsPeriod: true},
  'appraisal-grade': {
    title: 'Average appraisal grade',
    body: 'Average appraisal grade in the period. Grade uses the employment-status sort order from the appraisal.',
    valueLabel: 'Grade',
    needsPeriod: true}};

const GROUP_LABEL: Record<StatsBreakdownGroupBy, string> = {
  rank: 'Rank',
  'rank-category': 'Rank category',
  vessel: 'Vessel'};

const isKind = (v: string | undefined): v is StatsBreakdownKind =>
  v === 'age' || v === 'sign-on' || v === 'sign-off' || v === 'appraisal-grade';

const isGroupBy = (v: string | undefined): v is StatsBreakdownGroupBy =>
  v === 'rank' || v === 'rank-category' || v === 'vessel';

type ListTab = 'summary' | 'detail';

const StatisticsBreakdownPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const isDark = theme === 'dark';
  const { kind: kindParam, groupBy: groupByParam } = useParams<{ kind: string; groupBy: string }>();
  const kind = isKind(kindParam) ? kindParam : null;
  const groupBy = isGroupBy(groupByParam) ? groupByParam : null;
  const meta = kind ? KIND_META[kind] : null;

  const [vesselId, setVesselId] = useState('');
  const [periodFrom, setPeriodFrom] = useState(todayIso);
  const [periodTo, setPeriodTo] = useState(todayIso);
  const [applied, setApplied] = useState<{ vesselId: string; periodFrom: string; periodTo: string } | null>(
    null,
  );
  const [listTab, setListTab] = useState<ListTab>('summary');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);

  const listUrl =
    applied && kind && groupBy
      ? `/crew/reports/statistics-breakdown?kind=${encodeURIComponent(kind)}&groupBy=${encodeURIComponent(groupBy)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}${
          applied.vesselId ? `&vesselId=${encodeURIComponent(applied.vesselId)}` : ''
        }`
      : null;

  const { data, error, isLoading, mutate } = useSWR<StatsBreakdownResponse>(listUrl, fetcher, {
    revalidateOnFocus: false});

  const handleSearch = useCallback(() => {
    setApplied({ vesselId, periodFrom, periodTo });
    setSelectedGroupKey(null);
    setListTab('summary');
  }, [vesselId, periodFrom, periodTo]);

  const summaryColumns: Column<StatsBreakdownRow>[] = useMemo(() => {
    const showAvg = kind === 'age' || kind === 'appraisal-grade';
    const cols: Column<StatsBreakdownRow>[] = [
      {
        key: 'label',
        header: groupBy ? GROUP_LABEL[groupBy] : 'Group',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />},
      { key: 'count', header: 'Count', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ];
    if (showAvg) {
      cols.push(
        { key: 'avg', header: 'Average', sortable: true, render: (v) => <ReportTextCell value={v} /> },
        { key: 'min', header: 'Min', sortable: true, render: (v) => <ReportTextCell value={v} /> },
        { key: 'max', header: 'Max', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      );
    }
    return cols;
  }, [kind, groupBy]);

  const detailColumns: Column<StatsBreakdownDetailRow>[] = useMemo(() => {
    const cols: Column<StatsBreakdownDetailRow>[] = [
      {
        key: 'name',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link
            to={`/crew/${row.crewId}`}
            state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.name}
          </Link>
        )},
      {
        key: 'groupLabel',
        header: groupBy ? GROUP_LABEL[groupBy] : 'Group',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />},
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ];
    if (kind === 'age' || kind === 'appraisal-grade') {
      cols.push({
        key: 'value',
        header: meta?.valueLabel ?? 'Value',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />});
    }
    cols.push(
      {
        key: 'date',
        header: kind === 'age' ? 'Birth date' : 'Date',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      {
        key: 'extra',
        header: kind === 'appraisal-grade' ? 'Status' : kind === 'sign-off' ? 'Reason' : 'Note',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />},
    );
    return cols;
  }, [kind, groupBy, meta?.valueLabel, colors.primaryFg]);

  const details = useMemo(() => {
    const list = data?.details ?? [];
    if (!selectedGroupKey) return list;
    return list.filter((r) => r.groupKey === selectedGroupKey);
  }, [data?.details, selectedGroupKey]);

  if (!kind || !groupBy || !meta) {
    return <Navigate to="/crew/statistics" replace />;
  }

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{ title: `${meta.title} — ${GROUP_LABEL[groupBy]}`, body: meta.body }}
        onSearch={handleSearch}
        loading={isLoading}
        onRefresh={applied ? () => mutate() : undefined}
        context={
          data
            ? `${data.vessel?.vesselName ?? 'Fleet'}${
                meta.needsPeriod ? ` · ${data.periodFrom} → ${data.periodTo}` : ''
              } · ${data.summary.rowCount} rows · ${data.summary.groupCount} groups`
            : undefined
        }
      >
        <CrewReportFilterField label="Vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="All vessels…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
        {meta.needsPeriod ? (
          <>
            <CrewReportFilterField label="Period from" width="md">
              <DateInput name="periodFrom" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
            </CrewReportFilterField>
            <CrewReportFilterField label="Period to" width="md">
              <DateInput name="periodTo" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
            </CrewReportFilterField>
          </>
        ) : null}
      </CrewReportSearchHeader>

      <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
        {!applied ? (
          <CrewDataTablePrompt>Choose filters, then search.</CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt>
            <span className={isDark ? 'text-red-300' : 'text-red-600'}>Could not load statistics.</span>
          </CrewDataTablePrompt>
        ) : isLoading && !data ? (
          <CrewDataTablePrompt>Loading…</CrewDataTablePrompt>
        ) : data ? (
          <StatsKpiListLayout
            activeTab={listTab}
            onTabChange={(id) => {
              setListTab(id as ListTab);
              if (id === 'summary') setSelectedGroupKey(null);
            }}
            tabs={[
              { id: 'summary', label: 'By group', count: data.rows.length },
              { id: 'detail', label: 'Detail', count: details.length },
            ]}
            kpiGrid={
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <KpiTile label="Records" value={formatStatNum(data.summary.rowCount)} />
                <KpiTile label="Groups" value={formatStatNum(data.summary.groupCount)} />
                {kind === 'age' || kind === 'appraisal-grade' ? (
                  <>
                    <KpiTile
                      label="Average"
                      value={data.summary.avg == null ? '—' : formatStatNum(data.summary.avg, 2)}
                    />
                    <KpiTile
                      label="Min / Max"
                      value={
                        data.summary.min == null || data.summary.max == null
                          ? '—'
                          : `${formatStatNum(data.summary.min)} / ${formatStatNum(data.summary.max)}`
                      }
                    />
                  </>
                ) : (
                  <KpiTile label="Total events" value={formatStatNum(data.summary.rowCount)} />
                )}
              </div>
            }
          >
            {listTab === 'summary' ? (
              <DataTable
                data={data.rows}
                columns={summaryColumns}
                keyExtractor={(row) => row.key}
                emptyMessage="No data for this filter"
                onRowClick={(row) => {
                  setSelectedGroupKey(row.key);
                  setListTab('detail');
                }}
              />
            ) : (
              <DataTable
                data={details}
                columns={detailColumns}
                keyExtractor={(row) => row.id}
                emptyMessage="No detail rows"
                onRowClick={(row) => openCrew(row.crewId)}
                actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                actionsPosition="left"
              />
            )}
          </StatsKpiListLayout>
        ) : null}
      </div>
    </div>
  );
};

export default StatisticsBreakdownPage;
